import { TrashIcon } from "@phosphor-icons/react/dist/csr/Trash";
import { useState } from "react";
import { Button } from "@volca/ui/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@volca/ui/components/ui/dialog";
import { Label } from "@volca/ui/components/ui/label";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@volca/ui/components/ui/select";
import { Stack } from "@volca/ui/components/ui/stack";
import { Text } from "@volca/ui/components/ui/text";
import { cn } from "@volca/ui/lib/utils";
import { SETTINGS_CONTROLS } from "@/profitability/settings/control-classes";
import type { ProfitabilityReport } from "@/profitability/types";
import { reportError } from "@/profitability/utils";
import {
  type TemplateViewer,
  useTemplateViewers,
  useViewerMutations,
} from "./query";

const ViewerRow = ({
  viewer,
  busy,
  onRemove,
}: {
  viewer: TemplateViewer;
  busy: boolean;
  onRemove: () => void;
}) => (
  <li>
    <Stack direction="row" align="center" justify="between" gap={3}>
      <Stack gap={0} className="min-w-0">
        <Text className="[overflow-wrap:anywhere]">
          {viewer.user.full_name}
        </Text>
        <Text
          variant="caption"
          tone="muted"
          className="[overflow-wrap:anywhere]"
        >
          {viewer.user.email}
        </Text>
      </Stack>
      <Stack direction="row" align="center" gap={2} className="flex-none">
        <Button
          variant="ghost"
          size="icon"
          disabled={busy}
          aria-label={`Remove ${viewer.user.full_name}`}
          onClick={onRemove}
        >
          <TrashIcon aria-hidden />
        </Button>
      </Stack>
    </Stack>
  </li>
);

/**
 * Who outside finance may open this template: the managers an owner shares it
 * with (`ProfitabilityReportViewer`), each emailed when a period of it is
 * marked reviewed unless that is turned off. The share summary beside it is
 * for sending one period; this is lasting access.
 */
export const ManageAccessDialog = ({
  businessId,
  report,
  open,
  onOpenChange,
}: {
  businessId: string;
  report: ProfitabilityReport;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) => {
  const viewers = useTemplateViewers(businessId, report.id, open);
  const { add, remove } = useViewerMutations(businessId, report.id);
  const [userId, setUserId] = useState<string | null>(null);
  const shared = viewers.data?.viewers ?? [];
  const candidates = (viewers.data?.eligible_users ?? []).filter(
    (person) => !shared.some((viewer) => viewer.user.id === person.id),
  );
  const busy = add.isPending || remove.isPending;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className={cn(
          "max-h-[calc(100dvh-2rem)] overflow-y-auto overscroll-contain sm:max-w-lg",
          SETTINGS_CONTROLS,
        )}
      >
        <DialogHeader>
          <DialogTitle>Manage access</DialogTitle>
          <DialogDescription>
            Managers can view reports and update findings. Owners and finance
            can also run reports and change this agent.
          </DialogDescription>
        </DialogHeader>
        {viewers.isPending && (
          <Text tone="muted" role="status">
            Loading who has access…
          </Text>
        )}
        {viewers.isError && (
          <Text tone="destructive" role="alert">
            Access could not load. Try again. {reportError(viewers.error)}
          </Text>
        )}
        {viewers.data && (
          <Stack gap={4} align="stretch">
            {shared.length === 0 ? (
              <Text tone="muted">
                Owners and finance have access. Add a manager below.
              </Text>
            ) : (
              <Stack
                render={<ul aria-label="People with access" />}
                gap={3}
                align="stretch"
              >
                {shared.map((viewer) => (
                  <ViewerRow
                    key={viewer.id}
                    viewer={viewer}
                    busy={busy}
                    onRemove={() => remove.mutate(viewer.id)}
                  />
                ))}
              </Stack>
            )}
            {candidates.length > 0 ? (
              <Stack gap={1} align="stretch">
                <Label htmlFor="add-viewer">Add a manager</Label>
                <Stack direction="row" gap={2}>
                  <Select
                    value={userId}
                    items={candidates.map((person) => ({
                      value: person.id,
                      label: person.full_name,
                    }))}
                    onValueChange={(next) => setUserId(next)}
                  >
                    <SelectTrigger id="add-viewer" className="min-w-0 flex-1">
                      <SelectValue placeholder="Pick a person" />
                    </SelectTrigger>
                    <SelectContent className="pointer-coarse:[&_[data-slot=select-item]]:min-h-11">
                      <SelectGroup>
                        {candidates.map((person) => (
                          <SelectItem key={person.id} value={person.id}>
                            {person.full_name}
                          </SelectItem>
                        ))}
                      </SelectGroup>
                    </SelectContent>
                  </Select>
                  <Button
                    disabled={!userId || busy}
                    loading={add.isPending}
                    onClick={() => {
                      if (userId)
                        add.mutate(userId, {
                          onSuccess: () => setUserId(null),
                        });
                    }}
                  >
                    Add
                  </Button>
                </Stack>
              </Stack>
            ) : (
              <Text variant="caption" tone="muted">
                Everyone who can be added already has access. Managers are added
                under Users in business settings.
              </Text>
            )}
          </Stack>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Done
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
